import { IconButton, Menu, MenuItem, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { RiaFeeReportStatusObj } from "../../../redux-store/types/onBoarding";
import CustomTable from "../../investorOverview/common/customTable";
import { Order, PaginationObj, SortingObj, TableHeaderData } from "../../investorOverview/types";
import dayjs from "dayjs";
import { ActionIcon } from "../../../assets/SvgIcons";
import { useState } from "react";

interface ClientStmtTableProps {
    isLoading: boolean;
    tableData: Array<RiaFeeReportStatusObj>;
    handleTableSort: (orderBy: string, order: Order) => void;
    tablePaginationObj: PaginationObj;
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    tableSortingObj: SortingObj;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: RiaFeeReportStatusObj) => void;
}

const STATUS_COLOR_MAP: Record<string, string> = {
    success: "success.500",
    completed: "success.500",
    pending: "warning.500",
    failed: "error.500"
}

const INVOICE_STATUS_COLOR_MAP: Record<string, string> = {
    freeze: "primary.500",
    freezed: "primary.500",
    unfreeze: "primary.200",
    unfreezed: "primary.200",
    pending: "warning.500",
}

const headerData: TableHeaderData[] = [
    {
        id: 'rmName',
        label: 'RM Name',
        align: 'left',
        isSortable: true,
    },
    {
        id: 'familyName',
        label: 'Family Name',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'billingEntityName',
        label: 'Member Name',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'investorType',
        label: 'Type',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'period',
        label: 'Period',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'initiatedAt',
        label: 'Initiated Date',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'invoiceStatus',
        label: 'Invoice Status',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'status',
        label: 'Status',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'action',
        label: 'Action',
        align: 'center',
        isSortable: false,
    }
]

const ACTION_MENU_MAP: Record<string, Array<string>> = {
    success: [
        // "Download Report",
        // "Freeze",
        // "Lock"
    ],
    completed: [
        // "Download Report",
        // "Freeze",
        // "Lock"
    ],
    freeze: [
        "Download Report",
        // "Unfreeze",
        "Share Invoice",
        "Download Invoice"
    ],
    freezed: [
        "Download Report",
        // "Unfreeze",
        "Share Invoice",
        "Download Invoice"
    ],
    lock: [
        // "Share Invoice",
        "Download Invoice",
        "Download Report",
        "Unlock"
    ],
    locked: [
        // "Share Invoice",
        "Download Invoice",
        "Download Report",
        "Unlock"
    ],
    unfreeze: [
        // "Freeze",
        "Download Report"
    ],
    unfreezed: [
        // "Freeze",
        "Download Report"
    ],
    unlock: [
        "Download Report",
        // "Freeze",
        "Lock"
    ],
    unlocked: [
        "Download Report",
        // "Freeze",
        "Lock"
    ],
    pending: [],
    failed: [],
    all: [
        "Download Report"
    ]
}

function ActionMenu({
    row,
    handleMenuItemClick
}: {
    row: RiaFeeReportStatusObj;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: RiaFeeReportStatusObj) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item: string) => {
        handleMenuItemClick(item, row)

        handleClose()
    }

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-controls={open ? `action-menu-${row.id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                disabled={["pending", "failed"].includes(`${row?.status ?? ""}`.toLowerCase())}
                sx={{
                    '&.Mui-disabled svg': {
                        opacity: 0.38,
                    }
                }}
            >
                <ActionIcon />
            </IconButton>
            <Menu
                id={`action-menu-${row.id}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {(ACTION_MENU_MAP[`${row?.invoiceStatus ?? "All"}`.toLowerCase()] ?? []).map(item => (
                    <MenuItem
                        key={item}
                        onClick={() => handleItemClick(item)}
                    >
                        {item}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function DataTable({
    tableBodyData,
    handleMenuItemClick
}: {
    tableBodyData: Array<RiaFeeReportStatusObj>;
    handleMenuItemClick: (selectedMenuItem: string, currentRow: RiaFeeReportStatusObj) => void;
}): JSX.Element {
    const theme = useTheme()

    return (
        <>
            {
                tableBodyData.map((obj: RiaFeeReportStatusObj, index: number) => {
                    return (
                        <TableRow
                            key={`${obj.id}-${index}`}
                            sx={{
                                height: "4.5em",
                                '& td': {
                                    padding: "5px",
                                    height: "4.5em",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                },
                                '& td:first-of-type': {
                                    paddingLeft: "15px"
                                },
                            }}
                        >
                            <TableCell align="left">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.rmName || "-"}
                                </Typography>
                                {obj?.rmId ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.rmId})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.familyName || "-"}
                                </Typography>
                                {obj?.familyId ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.familyId})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.billingEntityName || "-"}
                                </Typography>
                                {obj?.billingEntityId ? (
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        ({obj.billingEntityId})
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {obj?.investorType || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.period || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {`${obj?.initiatedAt ?? ""}`.length ? dayjs(obj?.initiatedAt).format('DD/MM/YYYY HH:mm:ss') : "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: INVOICE_STATUS_COLOR_MAP[`${obj?.invoiceStatus ?? ""}`.toLowerCase()] ?? theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.invoiceStatus || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: STATUS_COLOR_MAP[`${obj?.status ?? ""}`.toLowerCase()] ?? theme.palette.text.primary,
                                    }}
                                >
                                    {obj?.status || "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <ActionMenu row={obj} handleMenuItemClick={handleMenuItemClick} />
                            </TableCell>
                        </TableRow >
                    )
                })
            }
        </>
    )
}

export default function ClientStmtTable({
    isLoading,
    tableData,
    handleTableSort,
    tablePaginationObj,
    handleTableRowsPerPageChange,
    handleTablePageChange,
    tableSortingObj,
    handleMenuItemClick
}: ClientStmtTableProps): JSX.Element {
    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableBodyData={tableData}
                    handleMenuItemClick={handleMenuItemClick}
                />
            }
            tableData={tableData}
            tableHeaderData={headerData}
            handleSort={handleTableSort}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "15px"
                }
            }}

            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={tablePaginationObj.totalRows}
            rowsPerPage={tablePaginationObj.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tablePaginationObj.currentPage}
            order={tableSortingObj.order}
            orderBy={tableSortingObj.orderBy}
        />
    )
}